import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ForeignKeyValidator } from 'src/app/components/core/_infrastructure/classes/foreignKeyValidator';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { NoWhiteSpaceValidator } from 'src/app/components/core/_infrastructure/classes/noWhiteSpaceValidator';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { GlobalService } from 'src/app/components/core/_infrastructure/classes/services/global.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { Tc } from 'src/app/components/core/_infrastructure/types/common';
import { CampaniaEspecificaService } from '../campaniaespecifica.service';
import { TCampaniaEspecificaViewModel } from '../campaniaespecifica.types';

@Component({
    selector: 'app-campaniaespecifica-wrapper-edit',
    templateUrl: './campaniaespecifica-wrapper-edit.component.html',
    styleUrls: ['./campaniaespecifica-wrapper-edit.component.scss'],
})
export class CampaniaEspecificaWrapperEditComponent extends ComponentBase implements OnInit {
    /**
     * @article: Reactive forms with smart dumb components
     * https://medium.com/angular-athens/reactive-forms-with-smart-dumb-components-48ebc81417c7
     */

    visibilitySpinner = false;
    campaniaEspecifica: TCampaniaEspecificaViewModel;
    formGroup: FormGroup;

    paises: Tc[];
    modelosCf: Tc[];
    idiomas: Tc[];

    private messageErrorAlreadyExist = 'Ya existe esta combinación de Campaña Específica y País';
    private successMessage = 'Campaña Específica modificada correctamente';
    private route = '/mantenimiento/campanias';

    activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const id: any = params.get('id');
        if (id) {
            this.getById(id);
        }
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private campaniaEspecificaService: CampaniaEspecificaService,
        private globalService: GlobalService,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private navMenuService: NavMenuService,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.getLists();
        this.buildForm();
    }

    private getLists(): void {
        this.getPaises();
        this.getModelosCf();
        this.getIdiomas();
    }

    /**
     * Creación del formulario con los datos de la campaña específica.
     * @return void
     */
    private buildForm(): void {
        this.formGroup = this.fb.group({
            basic: this.fb.group({
                Id: [null],
                Descripcion: [null, Validators.compose([Validators.required, NoWhiteSpaceValidator.cannotContainSpace])],
                IdPais: [{ value: '', disabled: true }, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
                IdModeloCf: [{ value: '', disabled: true }, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
                IdIdioma: [{ value: '', disabled: true }],
                FechaCreacion: [''],
            }),
        });
    }

    /**
     * Invoca a un método global de la API para obtener la lista de países
     * @return void
     */
    private getPaises(): void {
        this.globalService.getAllPaises().subscribe((items: Tc[]) => {
            this.paises = items;
        });
    }

    /**
     * Invoca a un método global de la API para obtener la lista de Modelos CF
     * @return void
     */
    private getModelosCf(): void {
        this.globalService.getModelosCf().subscribe((items: Tc[]) => {
            this.modelosCf = items;
        });
    }

    private getIdiomas(): void {
        this.globalService.getIdioma().subscribe(results => {
            this.idiomas = results;
        });
    }

    /**
     * Método que invoca a la API para cargar los datos de una campaña específica a partir de su id.
     * @param id number
     * @return void
     */
    private getById(id: number): void {
        this.campaniaEspecificaService
            .getById(id)
            .pipe(
                tap((ce: TCampaniaEspecificaViewModel) => {
                    this.campaniaEspecifica = ce;
                    this.campaniaEspecificaService.campaniaEspecifica = this.campaniaEspecifica;
                    this.navMenuService.updateMenuServiceForCampania(ce.Id!);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    this.formGroup.disable();
                    return of([]);
                }),
            )
            .subscribe(); // Sin subscribe, no se ejecuta la invocación al servicio
    }

    /**
     * Invocación de la API para salvar los cambios realizados en la campaña específica.
     * @return void
     */
    onSave(): void {
        this.visibilitySpinner = true;
        const model = { ...this.formGroup.value.basic };

        this.campaniaEspecificaService
            .save(model)
            .pipe(
                tap(() => {
                    this.visibilitySpinner = false;
                    this.addSuccessMessage(this.successMessage);
                    this.onBack();
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    if (error?.message?.indexOf(this.messageErrorAlreadyExist) != -1) {
                        this.formGroup.disable();
                    }
                    return of([]);
                }),
            )
            .subscribe(); // Sin subscribe, no se ejecuta la invocación al servicio
    }

    /**
     * Permite volver al usuario a la página con el listado de campañas específicas.
     * @return void
     */
    onBack(): void {
        this.router.navigate([this.route]);
    }

    /**
     * Getter para controlar la propiedad disabled del botón de guardar.
     * @return boolean
     */
    get formInvalid(): boolean {
        return !this.formGroup.valid || !this.formGroup.dirty || this.formGroup.pristine;
    }
}
